import { useMemo, useState } from "react";
import { getSendTypeTabButtonId } from "../constants";
import NotificationColumnFilters from "./NotificationColumnFilters";
import NotificationDetailsSheet from "./NotificationDetailsSheet";
import NotificationsList from "./NotificationsList";
import "./NotificationsQueueMainContent.scss";

/**
 * Send-type tab panel main content: column filters + {@link NotificationsList},
 * plus the {@link NotificationDetailsSheet} drawer for a selected row.
 *
 * @param {object} props
 * @param {import("../useNotifications").UseNotificationsProps} props.useNotificationsHookData Full {@link useNotifications} bag;
 *   this shell reads `sendType`, `notifications`, `columnFilters`, `setColumnFilters`; passes the same bag to {@link NotificationsList}.
 * @param {boolean} props.columnFiltersExpanded Whether the column filter builder is expanded.
 * @returns {import('react').JSX.Element}
 */
const NotificationsQueueMainContent = ({
    useNotificationsHookData,
    columnFiltersExpanded,
}) => {
    const {
        sendType,
        notifications,
        columnFilters,
        setColumnFilters,
        queueFilter,
        refreshNotificationsList,
    } = useNotificationsHookData;

    const [detailsOpen, setDetailsOpen] = useState(false);
    const [selectedNotificationId, setSelectedNotificationId] = useState(null);

    const selectedListRow = useMemo(
        () =>
            selectedNotificationId == null
                ? null
                : notifications.find((r) => r.id === selectedNotificationId) ??
                  null,
        [notifications, selectedNotificationId],
    );

    /**
     * Opens the details drawer for the clicked list row.
     * @param {{ id: string }} row Notification row from the infinite query.
     */
    const openDetails = (row) => {
        setSelectedNotificationId(row.id);
        setDetailsOpen(true);
    };

    /**
     * Syncs drawer open state; clears the selected id when the drawer closes.
     * @param {boolean} open Next open state from Radix.
     */
    const handleDetailsOpenChange = (open) => {
        setDetailsOpen(open);

        if (!open) {
            setSelectedNotificationId(null);
        }
    };

    return (
        <div
            className="notifications-queue-main-content__send-type-panel"
            id="notifications-send-type-panel"
            role="tabpanel"
            aria-labelledby={getSendTypeTabButtonId(sendType)}
        >
            <NotificationDetailsSheet
                open={detailsOpen}
                onOpenChange={handleDetailsOpenChange}
                notificationId={selectedNotificationId}
                listRow={selectedListRow}
                sendType={sendType}
                queueFilter={queueFilter}
                refreshNotificationsList={refreshNotificationsList}
            />
            <NotificationColumnFilters
                filters={columnFilters}
                onFiltersChange={setColumnFilters}
                expanded={columnFiltersExpanded}
                notifications={notifications}
            />
            <NotificationsList
                useNotificationsHookData={useNotificationsHookData}
                selectedNotificationId={selectedNotificationId}
                detailsOpen={detailsOpen}
                onRowActivate={openDetails}
            />
        </div>
    );
};

export default NotificationsQueueMainContent;
